import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormMultiCombobox } from "@/shared/ui/FormMultiCombobox";
import { SmartFileUpload } from "@/shared/ui/SmartFileUpload";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  Field,
  FormCheckbox,
  FormInput,
  FormRoot,
  FormTextarea,
  Modal,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import {
  FileIcon,
  GitBranchIcon,
  InfoIcon,
  PlusIcon,
  Settings,
  SparklesIcon,
  UploadIcon,
  X,
} from "lucide-react";
import * as React from "react";
import { z } from "zod";
import type {
  CreateTemplateCategoryDTO,
  UpdateTemplateCategoryDTO,
} from "../../domain/contracts/category";
import {
  useCreateTemplateCategory,
  useDeleteTemplateCategory,
  useTemplateCategoriesQuery,
  useUpdateTemplateCategory,
} from "../api/queries";
import { TemplateCategoryForm } from "./TemplateCategoryForm";
import { templateFormSamples } from "./templateFormSamples";

type DepartmentItem = { id: string; name: string };

const TemplateFormSchema = z.object({
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ຮ່າງເອກະສານ"),
  categoryId: z.string().optional(),
  description: z.string().optional(),
  usageNotes: z.string().optional(),
  departmentIds: z.array(z.string()).default([]),
  fileKey: z.string().optional().nullable(),
  version: z.string().optional(),
  changelog: z.string().optional(),
  keepOldVersionsActive: z.boolean().default(true),
});

export type TemplateFormValues = z.infer<typeof TemplateFormSchema>;

type TemplateFormProps = {
  initialValues?: Partial<TemplateFormValues>;
  onSubmit: (values: TemplateFormValues) => void;
  submitting?: boolean;
  isEdit?: boolean;
};

export function TemplateForm({
  initialValues,
  onSubmit,
  submitting = false,
  isEdit = false,
}: TemplateFormProps) {
  const { data: categories } = useTemplateCategoriesQuery();

  const [sampleIndex, setSampleIndex] = React.useState(0);
  const isDev =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  const methods = RHF.useForm<TemplateFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference issue with optional fields
    resolver: zodResolver(TemplateFormSchema) as any,
    defaultValues: {
      name: "",
      categoryId: undefined,
      description: "",
      usageNotes: "",
      departmentIds: [],
      fileKey: null,
      version: undefined,
      changelog: "",
      keepOldVersionsActive: true,
      ...initialValues,
    },
  });

  const fileKey = RHF.useWatch({ control: methods.control, name: "fileKey" });

  // Convert categories to select options
  const categoryOptions =
    categories?.map((cat) => ({
      label: cat.name,
      value: cat.id,
    })) ?? [];

  // Handle auto fill with sample data
  const handleAutoFill = () => {
    const sample = templateFormSamples[sampleIndex];
    if (sample) {
      Object.entries(sample).forEach(([key, value]) => {
        methods.setValue(key as keyof TemplateFormValues, value as never);
      });
      setSampleIndex((prev) => (prev + 1) % templateFormSamples.length);
      toast.success("ຕື່ມຂໍ້ມູນຕົວຢ່າງສຳເລັດ");
    }
  };

  return (
    <FormRoot<TemplateFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-6"
    >
      {/* Section 1: ข้อมูลแบบฟอร์ม */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <InfoIcon className="h-5 w-5" />
            ຂໍ້ມູນຮ່າງເອກະສານ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div data-tourid="form-name">
            <FormInput
              name="name"
              label="ຊື່ຮ່າງເອກະສານ"
              requiredMark
              placeholder="ກະລຸນາໃສ່ຊື່ຮ່າງເອກະສານ"
            />
          </div>

          <div className="flex items-center gap-2" data-tourid="form-category">
            <Field name="categoryId" label="ປະເພດຮ່າງເອກະສານ" className="flex-1">
              <RHF.Controller
                control={methods.control}
                name="categoryId"
                render={({ field }) => (
                  <SimpleSelect
                    value={field.value || ""}
                    onValueChange={(val) => field.onChange(val || undefined)}
                    options={categoryOptions}
                    placeholder="ເລືອກປະເພດຮ່າງເອກະສານ"
                  />
                )}
              />
            </Field>
            <CategoryManagementButton />
          </div>

          <div data-tourid="form-departments">
            <FormMultiCombobox<DepartmentItem>
              name="departmentIds"
              label="ພະແນກທີ່ໃຊ້"
              queryKey={["template-departments"]}
              queryFn={(args) =>
                fetchLookupForInfinite(
                  `${config.apiUrl}/departments/lookup`,
                  args,
                )
              }
              preloadQueryFn={(ids) =>
                Promise.all(
                  ids.map((id) =>
                    hydrateLookupItem(
                      `${config.apiUrl}/departments/lookup`,
                      id,
                    ),
                  ),
                ).then((items) =>
                  items.filter((item): item is DepartmentItem => item !== null),
                )
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກພະແນກ..."
              clearable
            />
          </div>

          <div data-tourid="form-description">
            <FormTextarea
              name="description"
              label="ຄຳອະທິບາຍສັ້ນໆ"
              placeholder="ອະທິບາຍສັ້ນໆວ່າໃຊ້ກັບວຽກອັນໃດ"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Section 2: ไฟล์แบบฟอร์ม */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UploadIcon className="h-5 w-5" />
            ຟາຍຮ່າງເອກະສານ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div data-tourid="form-file">
            <Field
              name="fileKey"
              label="ຟາຍຮ່າງເອກະສານ (PDF / Word / Excel)"
              hint={
                isEdit
                  ? "ອັບໂຫຼດຟາຍໃໝ່ເພື່ອແທນທີ່ຟາຍເກົ່າ (ອັບເວີຊັນ)"
                  : "ກະລຸນາອັບໂຫຼດຟາຍຮ່າງເອກະສານ"
              }
              requiredMark={!isEdit}
            >
              <RHF.Controller
                control={methods.control}
                name="fileKey"
                render={({ field }) => (
                  <div className="space-y-2">
                    <SmartFileUpload
                      keyPrefix="templates"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onSuccess={(key) => {
                        field.onChange(key);
                        methods.trigger("fileKey");
                      }}
                      buttonLabel="ອັບໂຫຼດຟາຍ"
                      hint={undefined}
                    />
                    {fileKey && (
                      <div className="flex items-center justify-between rounded-md border bg-muted/50 p-2">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate text-sm">{fileKey}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            field.onChange(null);
                            methods.trigger("fileKey");
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: การจัดการเวอร์ชัน */}
      {(fileKey || isEdit) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitBranchIcon className="h-5 w-5" />
              ການຈັດການເວີຊັນ
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div data-tourid="form-version">
              <FormInput
                name="version"
                label="ລະບຸເວີຊັນ"
                placeholder={isEdit ? "ຕົວຢ່າງ: 2.0, 1.1" : "ຕົວຢ່າງ: 1.0"}
                hint={
                  isEdit ? "ຖ້າບໍ່ໃສ່ ຈະເພີ່ມເລກເວີຊັນອັດຕະໂນມັດ" : "ຖ້າບໍ່ໃສ່ ຈະເລີ່ມທີ່ເວີຊັນ 1.0"
                }
              />
            </div>

            <div data-tourid="form-changelog">
              <FormTextarea
                name="changelog"
                label="ຫມາຍເຫດວ່າປັບອັນໃດ"
                placeholder="ບອກວ່າມີການປ່ຽນແປງຫຍັງໃນເວີຊັນນີ້"
                rows={3}
              />
            </div>

            {isEdit && (
              <div data-tourid="form-keep-old-versions">
                <FormCheckbox
                  name="keepOldVersionsActive"
                  label="ໃຫ້ເວີຊັນເກົ່າຍັງສາມາດໃຊ້ງານໄດ້"
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submit Button */}
      <div className="sticky bottom-2 flex justify-between border-t bg-background pt-4">
        {isDev && !isEdit && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="mr-2 h-4 w-4" />
            Auto Fill (Sample {sampleIndex + 1}/{templateFormSamples.length})
          </Button>
        )}
        <div className="flex gap-2">
          <div data-tourid="form-submit">
            <Button type="submit" isLoading={submitting}>
              {isEdit ? "ບັນທຶກການແກ້ໄຂ" : "ສ້າງຮ່າງເອກະສານ"}
            </Button>
          </div>
        </div>
      </div>
    </FormRoot>
  );
}

// Category Management Component
function CategoryManagementButton() {
  const canManage = useActionPermission([
    "template:create",
    "template:update",
    "template:delete",
  ]);
  const listModal = useDisclosure();
  const createModal = useDisclosure();
  const editModal = useDisclosure();
  const { data: categories, isLoading, refetch } = useTemplateCategoriesQuery();
  const createCategory = useCreateTemplateCategory();
  const deleteCategory = useDeleteTemplateCategory();
  const [editingCategory, setEditingCategory] = React.useState<{
    id: string;
    name: string;
    code: string;
    description: string | null;
  } | null>(null);
  const updateCategory = useUpdateTemplateCategory(editingCategory?.id ?? "");

  const handleOpenModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    listModal.open();
  };

  const handleCreate = () => {
    createModal.open();
    listModal.close();
  };

  const handleEdit = (category: {
    id: string;
    name: string;
    code: string;
    description: string | null;
  }) => {
    setEditingCategory(category);
    editModal.open();
    listModal.close();
  };

  const handleCreateSubmit = async (
    input: CreateTemplateCategoryDTO | UpdateTemplateCategoryDTO,
  ) => {
    await createCategory.mutateAsync(input as CreateTemplateCategoryDTO);
    createModal.close();
    listModal.open();
    await refetch();
  };

  const handleEditSubmit = async (
    input: CreateTemplateCategoryDTO | UpdateTemplateCategoryDTO,
  ) => {
    if (!editingCategory) return;
    await updateCategory.mutateAsync(input as UpdateTemplateCategoryDTO);
    editModal.close();
    setEditingCategory(null);
    listModal.open();
    await refetch();
  };

  const handleDelete = async (category: {
    id: string;
    name: string;
    code: string;
    description: string | null;
  }) => {
    const confirmed = await confirm({
      title: "ລຶບປະເພດຮ່າງເອກະສານ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບ "${category.name}"?`,
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: {
        variant: "destructive",
      },
    });
    if (confirmed) {
      try {
        await deleteCategory.mutateAsync(category.id);
        await refetch();
      } catch {
        // Error handled by mutation
      }
    }
  };

  if (!canManage) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={handleOpenModal}
        title="ຈັດການປະເພດຮ່າງເອກະສານ"
      >
        <Settings className="h-4 w-4" />
      </Button>

      {/* Categories List Modal */}
      <Modal
        open={listModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            listModal.close();
          }
        }}
        title="ຈັດການປະເພດຮ່າງເອກະສານ"
        description="ສ້າງ, ແກ້ໄຂ, ຫຼື ລຶບປະເພດຮ່າງເອກະສານ"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCreate}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              ສ້າງປະເພດໃໝ່
            </Button>
          </div>

          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              ກຳລັງໂຫຼດ...
            </div>
          ) : categories && categories.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              ຍັງບໍ່ມີປະເພດຮ່າງເອກະສານ
            </div>
          ) : (
            <div className="space-y-2">
              {categories?.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{category.name}</span>
                    {category.code && (
                      <span className="text-muted-foreground text-xs">
                        ({category.code})
                      </span>
                    )}
                    {category.description && (
                      <span className="text-muted-foreground text-xs">
                        {category.description}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(category)}
                    >
                      ແກ້ໄຂ
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(category)}
                    >
                      ລຶບ
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Create Category Modal */}
      <Modal
        open={createModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            createModal.close();
          }
        }}
        title="ສ້າງປະເພດຮ່າງເອກະສານ"
        description="ເພີ່ມປະເພດຮ່າງເອກະສານໃໝ່"
        size="md"
      >
        <TemplateCategoryForm
          onSubmit={handleCreateSubmit}
          submitting={createCategory.isPending}
        />
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        open={editModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            editModal.close();
            setEditingCategory(null);
          }
        }}
        title="ແກ້ໄຂປະເພດຮ່າງເອກະສານ"
        description="ແກ້ໄຂຂໍ້ມູນປະເພດຮ່າງເອກະສານ"
        size="md"
      >
        {editingCategory && (
          <TemplateCategoryForm
            initialValues={{
              name: editingCategory.name,
              code: editingCategory.code,
              description: editingCategory.description ?? undefined,
            }}
            onSubmit={handleEditSubmit}
            submitting={updateCategory.isPending}
          />
        )}
      </Modal>
    </>
  );
}
